import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles, Loader2, ArrowRight, Calendar, Clock, User, Tag, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { usePageMeta } from '../hooks/usePageMeta';

import { API } from '../config';

const DEFAULT_CATEGORIES = [
  'All',
  'Travel Guide',
  'Monsoon Journeys',
  'Food on Train',
  'Station Spotlight',
  'Tips & Hacks',
  'Vande Bharat',
  'Scenic Routes',
];

const Stories = () => {
  usePageMeta(
    'Train Travel Stories Tips and Guides | TrainPanda',
    'Explore train travel stories, railway tips, travel guides, food ideas, PNR information and useful insights for a better journey with TrainPanda.'
  );
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState('All');
  const [query, setQuery] = useState('');

  // Fetch dynamic categories
  useEffect(() => {
    axios.get(`${API}/story-categories`)
      .then((r) => {
        if (r.data?.categories && r.data.categories.length > 0) {
          setCategories(['All', ...r.data.categories]);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch stories
  useEffect(() => {
    setLoading(true);
    const catParam = activeCat !== 'All' ? `&category=${encodeURIComponent(activeCat)}` : '';
    axios.get(`${API}/stories?limit=40${catParam}`)
      .then((r) => setItems(r.data?.stories || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [activeCat]);

  const filtered = items.filter((s) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      s.title?.toLowerCase().includes(q) ||
      s.excerpt?.toLowerCase().includes(q) ||
      s.slug?.toLowerCase().includes(q) ||
      (s.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  });

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
      {/* Header Banner */}
      <div className="text-center max-w-3xl mx-auto mb-10">
        <div className="inline-flex items-center gap-2 bg-tp-violet-soft text-tp-violet-deep px-3.5 py-1 rounded-full text-xs font-extrabold mb-3">
          <Sparkles size={13} /> TrainPanda Travel Chronicles
        </div>
        <h1 className="text-3xl md:text-5xl font-black text-[#0B2E5C] tracking-tight leading-tight">
          India by Rail: Stories, Guides &amp; Route Chronicles
        </h1>
        <p className="text-gray-500 mt-2 text-base md:text-lg">
          Hand-picked railway travel journeys, scenic monsoon routes, station food diaries, and tips for travellers.
        </p>

        {/* Search Input */}
        <div className="mt-6 max-w-md mx-auto relative">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search stories by destination, train or food..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10 h-11 bg-white rounded-full border-gray-200 shadow-sm focus-visible:ring-tp-violet"
          />
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap items-center justify-center gap-2 pb-3 mb-8 max-w-5xl mx-auto px-2">
        {categories.map((cat) => {
          const active = activeCat === cat;
          return (
            <button
              key={cat}
              onClick={() => setActiveCat(cat)}
              className={`px-4 py-2 rounded-full text-xs font-extrabold whitespace-nowrap transition-all duration-200 cursor-pointer ${
                active
                  ? 'bg-gradient-to-r from-tp-violet to-tp-violet-deep text-white shadow-md shadow-tp-violet/25 ring-2 ring-tp-violet/30'
                  : 'bg-white text-gray-700 border border-gray-200 hover:border-tp-violet hover:text-tp-violet hover:bg-purple-50/50 shadow-xs'
              }`}
            >
              {cat}
            </button>
          );
        })}
      </div>

      {/* Stories Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((n) => (
            <div
              key={n}
              className="bg-white rounded-3xl border border-gray-100 card-shadow overflow-hidden flex flex-col p-0 transition-all"
            >
              {/* Image Shimmer */}
              <div className="aspect-[16/10] w-full shimmer-wave" />

              {/* Card Body Shimmer */}
              <div className="p-6 space-y-4 flex-1 flex flex-col justify-between">
                <div className="space-y-3">
                  {/* Category Pill */}
                  <div className="h-5 w-24 rounded-full shimmer-wave-purple" />
                  {/* Title */}
                  <div className="h-6 rounded-xl w-5/6 shimmer-wave" />
                  <div className="h-6 rounded-xl w-3/4 shimmer-wave" />
                  {/* Excerpt */}
                  <div className="space-y-2 pt-1">
                    <div className="h-3.5 rounded-lg w-full shimmer-wave" />
                    <div className="h-3.5 rounded-lg w-4/5 shimmer-wave" />
                  </div>
                </div>

                {/* Footer Metadata */}
                <div className="pt-4 border-t border-gray-100 flex items-center justify-between">
                  <div className="h-4 w-28 rounded-md shimmer-wave" />
                  <div className="h-4 w-16 rounded-md shimmer-wave" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400 max-w-md mx-auto bg-white rounded-3xl border border-gray-100 p-8">
          <Sparkles size={36} className="mx-auto text-tp-violet mb-3" />
          <p className="text-base font-bold text-gray-700">No stories found in this category.</p>
          <p className="text-xs text-gray-400 mt-1">Check back soon or try selecting another category above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((s) => (
            <StoryCard key={s.id || s.slug} s={s} />
          ))}
        </div>
      )}
    </div>
  );
};

export const StoryCard = ({ s }) => (
  <Link
    to={`/stories/${s.slug}`}
    className="bg-white rounded-3xl border border-gray-100 card-shadow overflow-hidden hover:border-tp-violet/50 hover:-translate-y-1.5 transition-all duration-300 flex flex-col group"
  >
    {/* Image Thumbnail */}
    <div className="aspect-[16/10] bg-gray-100 overflow-hidden relative">
      {s.hero_image ? (
        <img
          src={s.hero_image}
          alt={s.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = 'none'; }}
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#0B2E5C] to-[#1E4E8C] flex items-center justify-center text-white/30">
          <Sparkles size={36} />
        </div>
      )}
      <div className="absolute top-3 left-3 bg-white/95 backdrop-blur text-tp-violet-deep text-[10px] font-black px-2.5 py-1 rounded-full shadow-sm tracking-wider uppercase">
        {s.category || 'TRAVEL STORY'}
      </div>
    </div>

    {/* Content */}
    <div className="p-5 flex-1 flex flex-col justify-between">
      <div>
        <h3 className="font-black text-[#0B2E5C] text-lg leading-snug line-clamp-2 group-hover:text-tp-violet transition-colors">
          {s.title}
        </h3>
        <p className="text-xs text-gray-500 mt-2 line-clamp-2 leading-relaxed">
          {s.excerpt}
        </p>
      </div>

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 text-xs text-gray-400 font-semibold">
        <div className="flex items-center gap-1.5 text-gray-500">
          <Calendar size={12} className="text-tp-violet" />
          <span>{s.created_at ? new Date(s.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'Recent'}</span>
        </div>

        <div className="text-tp-coral font-bold flex items-center gap-1 group-hover:gap-2 transition-all">
          <span>Read Story</span>
          <ArrowRight size={13} />
        </div>
      </div>
    </div>
  </Link>
);

export default Stories;
